import React, { useEffect, useState } from 'react';
import qs from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import { useAuth } from 'hooks';
import { notification } from 'antd';
import SsoLayout from './SsoLayout';
import {
  headingStyle,
  textStyle,
  spinnerContainerStyle,
  spinnerSvg,
} from './styles';

const ALLOWED_EXTENSION_IDS = ['lbpneicehekpgkbkbaagmenaicffpfkk'];

const SsoCallback = () => {
  const location = useLocation();
  const { push } = useHistory();
  const { oktaLogIn, oktaLoginError, isAuthed, user } = useAuth();
  const [provider, setProvider] = useState('SSO');

  useEffect(() => {
    const validateCallback = async () => {
      try {
        // Parse the URL parameters
        const params = new URLSearchParams(location.search);
        const payload = Object.fromEntries(params.entries());

        // Detect provider from parameters or default to SSO
        let providerName = 'SSO';

        if (payload.provider) {
          providerName =
            payload.provider.charAt(0).toUpperCase() +
            payload.provider.slice(1);
        } else if (payload.state) {
          // Try to detect from state if it contains JSON
          try {
            const stateData = JSON.parse(payload.state);
            if (stateData.provider) {
              providerName =
                stateData.provider.charAt(0).toUpperCase() +
                stateData.provider.slice(1);
            }
          } catch (e) {
            // State is not JSON, keep default
          }
        }

        setProvider(providerName);

        if (payload.code) {
          // Use the SSO login endpoint for authentication
          oktaLogIn({
            code: payload.code,
            state: payload.state,
          });
        } else if (payload.error) {
          notification.error({
            message: `${providerName} Authentication Failed`,
            description:
              payload.error_description ||
              'Authentication was cancelled or failed.',
            duration: 6,
            placement: 'bottomLeft',
          });
          push('/login');
        } else {
          notification.error({
            message: `${providerName} Authentication Failed`,
            description: 'No authorization code received. Please try again.',
            duration: 6,
            placement: 'bottomLeft',
          });
          push('/login');
        }
      } catch (error) {
        console.error('Error processing SSO callback:', error);
        notification.error({
          message: 'Authentication Error',
          description:
            'Something went wrong during authentication. Please try again.',
          duration: 6,
          placement: 'bottomLeft',
        });
        push('/login');
      }
    };

    validateCallback();
  }, [location, oktaLogIn, push]);

  useEffect(() => {
    if (!isAuthed) return;

    // Restore login context saved before the SSO redirect (e.g. extension params)
    let loginContext = null;
    try {
      const raw = sessionStorage.getItem('ssoLoginContext');
      if (raw) {
        loginContext = JSON.parse(raw);
        sessionStorage.removeItem('ssoLoginContext');
      }
    } catch {
      // Malformed JSON – fall through to default redirect
    }

    if (!loginContext?.redirect) {
      push('/');
      return;
    }

    const { redirect, sessionId, queryParams } = loginContext;

    // Build the full redirect string the same way Login/index.js does
    let redirectString = `${redirect}?${qs.stringify(queryParams || {}, {
      encode: false,
    })}`;

    // Chrome-extension redirect: pass token + user back to the extension
    if (redirectString.startsWith('chrome-extension://')) {
      try {
        const url = new URL(redirectString);

        if (!ALLOWED_EXTENSION_IDS.includes(url.hostname)) {
          console.error('Untrusted extension redirect');
          push('/');
          return;
        }

        const token = localStorage.getItem('token') || '';
        const userName = user?.userName || '';

        url.searchParams.set('token', token);
        url.searchParams.set('user', userName);
        if (sessionId) {
          url.searchParams.set('sessionId', sessionId);
        }

        window.location.href = url.toString();
        return;
      } catch {
        push('/');
        return;
      }
    }

    // Normal in-app redirect
    push(redirectString);
  }, [isAuthed, push, user]);

  useEffect(() => {
    if (oktaLoginError) {
      const status = oktaLoginError.status;
      if (status === 400 || status >= 500) {
        notification.error({
          message: 'SSO Authentication Failed',
          description:
            'Something went wrong with your SSO authentication. Please try again or contact your account manager.',
          duration: 6,
          placement: 'bottomLeft',
        });
        push('/login');
      }
    }
  }, [oktaLoginError, push]);

  return (
    <SsoLayout>
      <div style={headingStyle}>Processing {provider} Authentication…</div>
      <div style={textStyle}>Please wait while we log you in securely.</div>
      <div style={spinnerContainerStyle}>{spinnerSvg}</div>
    </SsoLayout>
  );
};

export default SsoCallback;
